import { useContext, useState } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  Typography,
} from "@mui/material";
import { Truck, Menu, X } from "lucide-react";
import { Link } from "react-scroll";
import RscLogo from "../../../assests/logo/rscLogo";
import { getUsers } from "../../../utils/getUser";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/authContext";

const navItems = [
  { label: "About", to: "about" },
  { label: "FAQs", to: "faqs" },
  { label: "Contact", to: "contact" },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navigate = useNavigate();

  const { showToast } = useContext(AuthContext);

  const handleLogin = () => {
    const user = getUsers();
    if (!user) {
      navigate("/login");
      return;
    }

    switch (user?.role) {
      case "MEMBER":
        navigate("/user-dashboard");
        break;
      case "MANAGER":
        navigate("/manager-dashboard");
        break;
      case "SUPERADMIN":
        navigate("/admin-dashboard");
        break;
      case "WAREHOUSE_ADMIN":
        navigate("/warehouse/inbound");
        break;
      case "WAREHOUSE_STAFF":
        navigate("/warehouse/inbound");
        break;
      default:
        showToast("Invalid User", "error");
        navigate("/login");
    }
  };

  const drawer = (
    <Box sx={{ width: 250, bgcolor: "background.paper", height: "100%" }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <RscLogo />
          <Typography sx={{ fontSize: "1.4rem", fontWeight: "bold" }}>
            RSC
          </Typography>{" "}
        </Box>
        <IconButton onClick={handleDrawerToggle}>
          <X />
        </IconButton>
      </Box>
      <List>
        {navItems.map((item) => (
          <ListItem key={item.label} disablePadding>
            <Link
              to={item.to}
              smooth={true}
              duration={500}
              onClick={handleDrawerToggle}
              style={{ width: "100%" }}
            >
              <ListItemText
                primary={item.label}
                sx={{
                  textAlign: "center",
                  px: 3,
                  py: 2,
                  "&:hover": {
                    bgcolor: "rgba(255, 255, 255, 0.08)",
                    cursor: "pointer",
                  },
                }}
              />
            </Link>
          </ListItem>
        ))}
        <ListItem sx={{ px: 2, py: 1 }}>
          <Button
            fullWidth
            variant="contained"
            size="small"
            sx={{ mb: 1 }}
            onClick={handleLogin}
          >
            Login
          </Button>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          bgcolor: "background.paper",
          boxShadow: "none",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <Container>
          <Toolbar sx={{ justifyContent: "space-between", px: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
              }}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              <RscLogo />
              <Typography sx={{ fontSize: "1.4rem", fontWeight: "bold" }}>
                RSC
              </Typography>{" "}
            </Box>

            {isMobile ? (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
              >
                <Menu />
              </IconButton>
            ) : (
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                {navItems.map((item) => (
                  <Link
                    key={item.label}
                    to={item.to}
                    smooth={true}
                    duration={500}
                  >
                    <Button variant="text">{item.label}</Button>
                  </Link>
                ))}
                <Button variant="contained" size="small" onClick={handleLogin}>
                  Login
                </Button>
              </Box>
            )}
          </Toolbar>
        </Container>

        <Drawer
          variant="temporary"
          anchor="right"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: 250 },
          }}
        >
          {drawer}
        </Drawer>
      </AppBar>
    </>
  );
}
